/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import { Divider, breakpoints } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import { useStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

const styles = {
  wrapper: (applyLightShadow?: boolean) => css`
    ${breakpoints.up('md')} {
      border: 1px solid var(--cds-color-grey-300);
      border-radius: var(--cds-spacing-100);
      margin: -76px auto 0;
      z-index: 1;
      position: relative;
      background-color: var(--cds-color-white-0);
      box-shadow: ${applyLightShadow
        ? '0 0 var(--cds-spacing-100) 2px rgb(0 0 0 / 8%)'
        : '0 var(--cds-spacing-100) var(--cds-spacing-200) 0 rgb(0 0 0 / 16%)'};
    }
  `,
  items: css`
    ${breakpoints.up('md')} {
      display: flex;
      flex: 1 1 0px;
      align-items: stretch;
      padding: var(--cds-spacing-300) 0;
      justify-content: center;
    }
  `,
  desktopItem: css`
    flex-grow: 1;
    display: flex;
    padding: 0 var(--cds-spacing-100);

    & + & {
      border-left: 1px solid var(--cds-color-grey-300);
    }

    .cds-ContextualHelp-container {
      /* keeps subtitle CTAs on the same line for desktop */
      display: flex !important;
    }
  `,
  itemWrapper: css`
    align-self: center;
    margin: 0 auto;
    min-width: 120px;
    max-width: 200px;
  `,
  mobileItem: css`
    & + & {
      margin-top: var(--cds-spacing-200);
    }
  `,
  mobileHorizontalDivider: css`
    width: 100%;
    left: 0;
    position: absolute;
    margin: var(--cds-spacing-300) 0;
    height: 2px;
  `,
  mobileItemStreamlinedXDP: css`
    display: flex;
    align-items: center;

    & + & {
      margin-top: var(--cds-spacing-150);
    }
  `,
};

type Props = {
  keyInformationItems: React.ReactNode[];
  applyLightShadow?: boolean;
};

const KeyInformationSpecializationHorizontalModule = ({ keyInformationItems, applyLightShadow }: Props) => {
  const userAgent = useUserAgent();
  const isStreamlinedXDP = useStreamlinedXDP();

  const keyInfoItems = keyInformationItems.filter((item) => {
    return item !== false;
  });

  if (!keyInfoItems.length) {
    return null;
  }

  const renderDesktopInfoItems = () => {
    return keyInfoItems.map((keyInfoItem, index) => (
      <div key={`key-info-${index}`} css={styles.desktopItem}>
        <div css={styles.itemWrapper}>{keyInfoItem}</div>
      </div>
    ));
  };

  const renderMobileInfoItems = () => {
    return keyInfoItems.map((keyInfoItem, index) => (
      <div key={`key-info-${index}`} css={isStreamlinedXDP ? styles.mobileItemStreamlinedXDP : styles.mobileItem}>
        {keyInfoItem}
        {index === keyInfoItems.length - 1 && !isStreamlinedXDP && (
          <Divider orientation="horizontal" css={styles.mobileHorizontalDivider} />
        )}
      </div>
    ));
  };

  return (
    <div css={styles.wrapper(applyLightShadow)}>
      <div css={styles.items}>
        <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
          {(matches) => (matches ? renderMobileInfoItems() : renderDesktopInfoItems())}
        </Media>
      </div>
    </div>
  );
};

export default KeyInformationSpecializationHorizontalModule;
